/**
 * Landing page content constants
 * All text content for the landing pages is centralized here
 */

// Navigation link type
export interface NavLink {
  label: string;
  path: string;
  disabled: boolean;
  external?: boolean;
}

// Navigation
export const NAV_LINKS: NavLink[] = [
  { label: 'Home', path: '/', disabled: false },
  { label: 'About', path: '/about', disabled: false },
  { label: 'Try Demo', path: '/demo', disabled: false },
  { label: 'Contact', path: '/contact', disabled: false },
  { label: 'Guide', path: '', disabled: false, external: true },
];

// Hero Section
export const HERO_CONTENT = {
  title: 'Lecturo',
  subtitle: 'Managing student feedback',
  description:
    'A research-based approach to student feedback that enables teachers to improve their courses while they are still running - not after. Developed at the University of Bergen and proven in real classroom settings.',
  primaryCta: 'Try Demo',
  secondaryCta: 'Learn More',
} as const;

// Features
export interface Feature {
  icon: string;
  title: string;
  description: string;
}

export const KEY_FEATURES: Feature[] = [
  {
    icon: '🔄',
    title: 'Formative, Not Summative',
    description:
      'Unlike end-of-semester surveys, collect feedback throughout the course so teachers can make improvements that benefit current students.',
  },
  {
    icon: '⚡',
    title: 'Quick & Low-Effort',
    description:
      'Inspired by airport feedback terminals, students respond in seconds with intuitive emoji-based ratings - preventing evaluation fatigue.',
  },
  {
    icon: '💬',
    title: 'Closes the Feedback Loop',
    description:
      'Teachers can discuss results with students and implement changes, creating a dialogue that improves learning outcomes.',
  },
  {
    icon: '🎯',
    title: 'Student Self-Reflection',
    description:
      'Questions prompt students to reflect on their own preparation and participation, recognizing learning as a shared responsibility.',
  },
];

// How It Works
export interface Step {
  number: number;
  title: string;
  description: string;
}

export const HOW_IT_WORKS_STEPS: Step[] = [
  {
    number: 1,
    title: 'Display the QR Code',
    description:
      'At the end of class, the lecturer displays a QR code on the screen. Students scan it with their phones.',
  },
  {
    number: 2,
    title: 'Students Respond in Seconds',
    description:
      'Two simple questions: "How was your learning outcome?" and "How well did you prepare?" Answered with intuitive emoji ratings.',
  },
  {
    number: 3,
    title: 'Course Representative Reviews',
    description:
      'A student representative filters inappropriate comments, ensuring feedback is constructive and actionable.',
  },
  {
    number: 4,
    title: 'Teacher Receives Insights',
    description:
      'Automatic summary with visualizations shows learning outcomes at a glance, with written comments for context.',
  },
  {
    number: 5,
    title: 'Discuss & Adjust',
    description:
      'Teachers can discuss results with students and adapt their teaching methods while the course is still running.',
  },
  {
    number: 6,
    title: 'Track Progress Over Time',
    description:
      'Monitor how learning outcomes evolve throughout the semester and identify what interventions work.',
  },
];

// CTA Section
export const CTA_CONTENT = {
  title: 'Ready to Make Feedback Formative?',
  description:
    'Join institutions using research-backed methodology to improve teaching while courses are still running.',
  primaryCta: 'Request Demo',
  secondaryCta: 'Contact Us',
} as const;

// About Page
export const ABOUT_CONTENT = {
  hero: {
    title: 'About Lecturo',
    subtitle:
      'A student initiative turned research-backed methodology for improving higher education.',
  },
  mission: {
    title: 'Born from Research',
    paragraphs: [
      'Lecturo emerged from a fundamental insight: most student evaluations in university STEM education arrive too late. Traditional end-of-semester surveys are summative - they provide a final snapshot, but the feedback comes too late to benefit the students who gave it.',
      'Developed at the Department of Geoscience, University of Bergen, our approach is different. We enable formative evaluation - continuous feedback throughout the semester that allows teachers to adjust their teaching methods in real-time. The results can be discussed with students, creating a genuine dialogue that improves learning outcomes.',
      'What started as a student initiative has been tested in over 20 courses, from large introductory bachelor courses to smaller master-level seminars. The consensus among both students and teaching staff is clear: this method is a valuable tool to improve teaching.',
    ],
  },
  research: {
    title: 'The Research Behind Lecturo',
    paragraphs: [
      'The quality of university STEM education has been a topic of research and policy discussion for decades. While student evaluations of teaching (SETs) have existed since the 1920s, they have evolved primarily into summative end-of-semester surveys - often used for administrative decisions about tenure and promotion rather than improving teaching.',
      'Our research addresses a gap identified in academic literature: formative student evaluation of teaching remains much less common and has not achieved the institutional visibility of summative evaluation, despite being potentially very useful for improving teaching, course content, and format.',
      'Teachers in our studies preferred this formative evaluation method over traditional summative evaluation. It requires minimal extra work, provides actionable insights, and most importantly - helps current students, not just future ones.',
    ],
  },
} as const;

export interface ValueItem {
  icon: string;
  title: string;
  description: string;
}

export const OUR_VALUES: ValueItem[] = [
  {
    icon: '🎓',
    title: 'Student-Centered',
    description:
      'Developed by students, for students. This project began as a student initiative to improve their own learning experience.',
  },
  {
    icon: '🔬',
    title: 'Evidence-Based',
    description:
      'Grounded in pedagogical research and continuously validated through real-world implementation.',
  },
  {
    icon: '🔄',
    title: 'Continuous Improvement',
    description:
      'Feedback should drive change during the course, not after. We believe in formative, not summative evaluation.',
  },
  {
    icon: '💬',
    title: 'Constructive Dialogue',
    description:
      'Course representatives filter feedback to ensure it is constructive and focused on improving learning outcomes.',
  },
];

// Demo Page
export const DEMO_CONTENT = {
  hero: {
    title: 'Try Lecturo Now',
    subtitle: 'Get instant access to a fully-functional demo environment with sample data.',
  },
  formTitle: 'Get Instant Access',
  formDescription:
    'Fill out the form below and get immediate access to your own demo environment for 48 hours.',
} as const;

export interface ExpectationStep {
  number: number;
  title: string;
  description: string;
}

export const WHAT_TO_EXPECT: ExpectationStep[] = [
  {
    number: 1,
    title: 'Instant Setup',
    description: 'Your demo environment is created immediately with pre-populated sample data.',
  },
  {
    number: 2,
    title: 'Full Lecturer Access',
    description: 'Explore all features: create surveys, manage instances, and view reports.',
  },
  {
    number: 3,
    title: 'Realistic Data',
    description: 'Two sample surveys with realistic student responses to see how insights work.',
  },
  {
    number: 4,
    title: '48-Hour Access',
    description: 'Plenty of time to explore and evaluate Lecturo for your institution.',
  },
];

// Contact Page
export const CONTACT_CONTENT = {
  hero: {
    title: 'Contact Us',
    subtitle: "We'd love to hear from you. Get in touch with our team.",
  },
  info: {
    email: 'ingelinn.gundersen@uib.no',
  },
} as const;

export interface FAQItem {
  question: string;
  answer: string;
}

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'Who is behind Lecturo?',
    answer:
      'Lecturo is a student-driven project from the Department of Geoscience at the University of Bergen. It started as an initiative to improve our own learning experience.',
  },
  {
    question: 'How does the feedback process work?',
    answer:
      'At the end of class, a QR code is displayed. Students scan it and answer two quick questions about their learning outcome and preparation. A course representative reviews comments before they reach the teacher.',
  },
  {
    question: 'Is student feedback anonymous?',
    answer:
      'Yes, all responses are anonymous. The system is designed so that teachers see aggregated results and filtered comments, not individual student identities.',
  },
  {
    question: 'Can I try Lecturo at my institution?',
    answer:
      'We are currently in early stages and primarily used at the University of Bergen. If you are interested in piloting Lecturo, please reach out to us.',
  },
];

// Footer link types
export interface FooterLink {
  label: string;
  path?: string;
  external?: boolean;
  disabled?: boolean;
  getUrl?: () => string;
}

// Footer
export const FOOTER_CONTENT = {
  description:
    'Research-backed formative course evaluation through continuous student feedback. Developed at the University of Bergen.',
  quickLinks: [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Try Demo', path: '/demo', disabled: false },
    { label: 'Contact', path: '/contact' },
    { label: 'Guide', external: true },
  ] as FooterLink[],
  legalLinks: [
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Terms of Service', path: '/terms' },
  ],
  contact: {
    email: 'ingelinn.gundersen@uib.no',
  },
  copyright: `© ${new Date().getFullYear()} Lecturo. All rights reserved.`,
} as const;
